import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography, Chip } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import Swal from "sweetalert2";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../store";

import {
  setPage,
  setRowsPerPage,
  setSearchTerm,
  setDateRange,
} from "../../store/slices/tableSlice";

import DataToolbar from "../../components/common/DataToolbar";
import DataTable from "../../components/common/DataTable";
import BackdropLoader from "../../components/common/BackdropLoader";

import {
  useGetUsersQuery,
  useDeleteUserMutation,
  useGetAllRolesQuery,
} from "../../api/services/userManagementAPI";

import UserFormModal from "./form/UserFormModal";
import { useDebounce } from "../../hooks/useDebounce";
import { useLazyGetAllPlantsDetailsQuery } from "../../api/services/plantApi";

interface UserRow {
  id: string;
  userName: string;
  employeeId: string;
  email: string;
  plant: string;
  role: string;
}

const UserManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  /* ---------------- Redux table state ---------------- */
  const { searchTerm, dateRange, rowsPerPage, currentPage } = useSelector(
    (state: any) => state.table
  );

  /* ---------------- Local UI state ---------------- */
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [plantFilter, setPlantFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /* ---------------- API ---------------- */
  const debouncedSearch = useDebounce(searchTerm, 500);
  
  const { data: rolesData,} = useGetAllRolesQuery();

  console.log("User Roles", rolesData);
  

  const [getPlants, { data: plantsData }] = useLazyGetAllPlantsDetailsQuery();

  const { data, isLoading, isFetching, refetch } = useGetUsersQuery({
    page: currentPage,
    limit: rowsPerPage,
    search: debouncedSearch || undefined,
    sortField: sortField || undefined,
    sortOrder: sortOrder || undefined,
    plant: plantFilter || undefined,
    designation: designationFilter || undefined,
  });

  

  const [deleteUser, { isLoading: isDeleteLoading }] = useDeleteUserMutation();

  /* ---------------- Derived data ---------------- */
  const usersData: UserRow[] = useMemo(() => {
    if (!data?.users) return [];
    return data.users.map((u: any) => ({
      id: String(u.id),
      userName: u.userName,
      employeeId: u.employeeId,
      email: u.email,
      plant: u.plant,
      role: u.role,
    }));
  }, [data]);

  useEffect(() => {
    getPlants({ pageNum: 1, search: "" });
  }, [getPlants]);

  const plantOptions = useMemo(() => {
    if (!plantsData?.plants) return [];

    return plantsData.plants.map((p: any) => p.name); // A110, A112
  }, [plantsData]);

  const plantOptionsWithAll = useMemo(() => {
    return ["", ...plantOptions]; // "" = All Plant
  }, [plantOptions]);

  const desginationOptions = useMemo(() => {
    if (!rolesData?.data) return [];

    return rolesData.data.map((role: any) => role.label);
  }, [rolesData])

  const desginationOptionsWithAll = useMemo(() => {
    return ["", ...desginationOptions];
  }, [desginationOptions])

  // const uniquePlants: string[] = data?.distinctPlants ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.count || 0) / rowsPerPage));

  /* ---------------- Handlers ---------------- */
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    dispatch(setPage(1));
  };

  const handleSelectRow = (id: string) => {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleEdit = (user: any) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = async (user: any) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${user.userName}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteUser(user.id).unwrap();
      Swal.fire("Deleted!", "User deleted successfully", "success");
      setSelectedUsers([]);
      refetch();
    } catch (err: any) {
      Swal.fire("Error", err?.data?.message || "Delete failed", "error");
    }
  };

  const handleBulkDelete = async () => {
    const result = await Swal.fire({
      title: "Delete selected users?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(selectedUsers.map((id) => deleteUser(id).unwrap()));
      Swal.fire("Success", "Users deleted", "success");
      setSelectedUsers([]);
      refetch();
    } catch {
      Swal.fire("Error", "Some users could not be deleted", "error");
    }
  };

  /* ---------------- Columns (DataTable compatible) ---------------- */
  const columns = [
    { key: "userName", label: "User Name", sortable: true },
    { key: "employeeId", label: "Employee ID", sortable: true },
    { key: "email", label: "E-mail", sortable: true },
    {
      key: "plant",
      label: "Plant",
      sortable: true,
      filter: {
        value: plantFilter,
        options: plantOptionsWithAll,
        onChange: setPlantFilter,
      },
    },
    {
      key: "role",
      label: "Designation",
      sortable: true,
      filter: {
        value: designationFilter,
        options: desginationOptionsWithAll,
        onChange: setDesignationFilter,
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <BackdropLoader openStates={isLoading || isFetching || isDeleteLoading} />

      {/* ---------- Header ---------- */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight={600}>
          User Management
        </Typography>

        <Box display="flex" gap={1}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => {
              setEditingUser(null);
              setIsModalOpen(true);
            }}
          >
            Add User
          </Button>

          {selectedUsers.length > 0 && (
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
            >
              Delete All
            </Button>
          )}
        </Box>
      </Box>

      {/* ---------- Toolbar ---------- */}
      <DataToolbar
        searchTerm={searchTerm}
        setSearchTerm={(val) => dispatch(setSearchTerm(val))}
        dateRange={dateRange}
        setDateRange={(val) => dispatch(setDateRange(val))}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={(val) => dispatch(setRowsPerPage(val))}
        currentPage={currentPage}
        setCurrentPage={(val) => dispatch(setPage(val))}
        totalPages={totalPages}
      />

      {/* ---------- Table ---------- */}
      <Box
        sx={{
          mt: 2,
          bgcolor: "#fff",
          borderRadius: 2,
          border: "1px solid #ddd",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #eee",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
          }}
        >
          Users
          <Chip label={data?.count ?? 0} size="small" />
        </Box>

        <DataTable
          columns={columns}
          data={usersData}
          getRowId={(row) => row.id}
          showCheckbox
          selectedRows={selectedUsers}
          onSelectRow={handleSelectRow}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
          emptyMessage="No users found"
          actionRenderer={(row) => (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={() => handleEdit(row)}>
                <EditOutlinedIcon />
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => handleDelete(row)}
              >
                <DeleteOutlineOutlinedIcon />
              </Button>
            </Box>
          )}
        />
      </Box>

      {/* ---------- Modal ---------- */}
      <UserFormModal
        open={isModalOpen}
        user={editingUser}
        mode={editingUser ? "edit" : "create"}
        onClose={() => {
          setIsModalOpen(false);
          setEditingUser(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setEditingUser(null);
          refetch();
        }}
      />
    </Box>
  );
};

export default UserManagement;
